'use client';

import { useState, useCallback } from 'react';
import ListingFilters from './ListingFilters';
import ListingsContent from './ListingsContent';
import type { MLSProperty, SortOption } from '@/lib/listings';
interface ListingsSearchClientProps {
  // Initial server-rendered data
  initialListings: MLSProperty[];
  initialTotal: number;
  initialTotalPages: number;
  initialPage: number;
  initialSearchParams: string;
  initialSort: SortOption;
  // Filter state from URL
  keyword?: string;
  status?: string;
  propertyType?: string;
  propertySubType?: string;
  selectedCities: string[];
  neighborhood?: string;
  minPrice?: number;
  maxPrice?: number;
  beds?: number;
  baths?: number;
  ourTeam?: boolean;
  // Dropdown options
  statuses: string[];
  propertyTypes: string[];
  propertySubTypes: string[];
  cities: string[];
  neighborhoods: string[];
  showOurTeamFilter: boolean;
  hasLocationFilter: boolean;
  template: string;
  listingsPerRow?: 2 | 3;
  googleMapsApiKey?: string;
  mlsWithVideos?: string[];
  mlsWithMatterport?: string[];
  teamAgentMlsIds?: string[];
}
export default function ListingsSearchClient({
  initialListings,
  initialTotal,
  initialTotalPages,
  initialPage,
  initialSearchParams,
  initialSort,
  keyword,
  status,
  propertyType,
  propertySubType,
  selectedCities,
  neighborhood,
  minPrice,
  maxPrice,
  beds,
  baths,
  ourTeam,
  statuses,
  propertyTypes,
  propertySubTypes,
  cities,
  neighborhoods,
  showOurTeamFilter,
  hasLocationFilter,
  template,
  listingsPerRow,
  googleMapsApiKey,
  mlsWithVideos: initialMlsWithVideos,
  mlsWithMatterport: initialMlsWithMatterport,
  teamAgentMlsIds: initialTeamAgentMlsIds
}: ListingsSearchClientProps) {
  const [listings, setListings] = useState(initialListings);
  const [total, setTotal] = useState(initialTotal);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [currentSort, setCurrentSort] = useState(initialSort);
  const [searchParams, setSearchParams] = useState(initialSearchParams);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [locationFilter, setLocationFilter] = useState(hasLocationFilter);
  const [mlsWithVideos, setMlsWithVideos] = useState<string[]>(initialMlsWithVideos || []);
  const [mlsWithMatterport, setMlsWithMatterport] = useState<string[]>(initialMlsWithMatterport || []);
  const [teamAgentMlsIds, setTeamAgentMlsIds] = useState<string[]>(initialTeamAgentMlsIds || []);
  const fetchListings = useCallback(async (params: URLSearchParams, {
    keepPage
  }: {
    keepPage?: boolean;
  } = {}) => {
    if (!keepPage) {
      params.delete('page');
    }

    // Update URL without navigation
    const qs = params.toString();
    const url = qs ? `/real-estate-for-sale?${qs}` : '/real-estate-for-sale';
    window.history.replaceState(null, '', url);
    setLoading(true);
    try {
      const res = await fetch(`/api/listings/search?${params.toString()}`);
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      setListings(data.listings || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 0);
      setCurrentPage(parseInt(params.get('page') || '1', 10));
      setCurrentSort(params.get('sort') as SortOption || 'newest');
      setSearchParams(params.toString());
      setLocationFilter(!!(params.get('city') || params.get('neighborhood')));
      if (data.mlsWithVideos) setMlsWithVideos(data.mlsWithVideos);
      if (data.mlsWithMatterport) setMlsWithMatterport(data.mlsWithMatterport);
      if (data.teamAgentMlsIds) setTeamAgentMlsIds(data.teamAgentMlsIds);
    } catch (err) {
      console.error('Error fetching listings:', err);
    } finally {
      setLoading(false);
    }
  }, []);
  const handleFilterChange = useCallback((params_0: URLSearchParams) => {
    fetchListings(params_0);
  }, [fetchListings]);
  const handleLoadMore = useCallback(async () => {
    if (loadingMore) return;
    const nextPage = currentPage + 1;
    const params_1 = new URLSearchParams(searchParams);
    params_1.set('page', nextPage.toString());
    setLoadingMore(true);
    try {
      const res_0 = await fetch(`/api/listings/search?${params_1.toString()}`);
      if (!res_0.ok) throw new Error('Search failed');
      const data_0 = await res_0.json();
      setListings(prev => [...prev, ...(data_0.listings || [])]);
      setTotal(data_0.total || 0);
      setTotalPages(data_0.totalPages || 0);
      setCurrentPage(nextPage);
      if (data_0.mlsWithVideos) setMlsWithVideos(prev_0 => [...new Set([...prev_0, ...data_0.mlsWithVideos])]);
      if (data_0.mlsWithMatterport) setMlsWithMatterport(prev_1 => [...new Set([...prev_1, ...data_0.mlsWithMatterport])]);
      if (data_0.teamAgentMlsIds) setTeamAgentMlsIds(prev_2 => [...new Set([...prev_2, ...data_0.teamAgentMlsIds])]);
    } catch (err_0) {
      console.error('Error loading more listings:', err_0);
    } finally {
      setLoadingMore(false);
    }
  }, [currentPage, searchParams, loadingMore]);
  const handleSortChange = useCallback((newSort: SortOption) => {
    const params_2 = new URLSearchParams(searchParams);
    if (newSort === 'newest') {
      params_2.delete('sort');
    } else {
      params_2.set('sort', newSort);
    }
    params_2.delete('page');
    fetchListings(params_2);
  }, [searchParams, fetchListings]);
  return <div className="h-full flex flex-col overflow-hidden">
      {/* Filters */}
      <div className="bg-white shadow-sm flex-shrink-0">
        <div className="px-4 pt-5 pb-1 sm:px-6 lg:px-8">
          <ListingFilters keyword={keyword} status={status} propertyType={propertyType} propertySubType={propertySubType} selectedCities={selectedCities} neighborhood={neighborhood} minPrice={minPrice} maxPrice={maxPrice} beds={beds} baths={baths} statuses={statuses} propertyTypes={propertyTypes} propertySubTypes={propertySubTypes} cities={cities} initialNeighborhoods={neighborhoods} ourTeam={ourTeam} showOurTeamFilter={showOurTeamFilter} onFilterChange={handleFilterChange} />
        </div>
      </div>

      {/* Listings Content with loading overlay */}
      <div className="relative flex-1 min-h-0">
        {loading && <div className="absolute inset-0 bg-white/60 z-10 flex items-start justify-center pt-20">
            <div className="flex items-center gap-2 text-gray-600">
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className="text-sm">Loading listings...</span>
            </div>
          </div>}
        <ListingsContent listings={listings} currentPage={currentPage} totalPages={totalPages} total={total} searchParams={searchParams} currentSort={currentSort} hasLocationFilter={locationFilter} template={template as any} listingsPerRow={listingsPerRow} onSortChange={handleSortChange} onLoadMore={handleLoadMore} isLoadingMore={loadingMore} googleMapsApiKey={googleMapsApiKey} mlsWithVideos={mlsWithVideos} mlsWithMatterport={mlsWithMatterport} teamAgentMlsIds={teamAgentMlsIds} />
      </div>
    </div>;
}